/**
 * Thin axios wrapper.
 *
 * Authentication: we run two auth strategies in parallel so the app works in
 * ALL browser contexts:
 *
 *   1. httpOnly cookies (the default, most secure). Set automatically by the
 *      backend on login/register/google-callback.
 *
 *   2. Bearer token in Authorization header, pulled from localStorage. This
 *      is the fallback that keeps us working when the preview is rendered
 *      inside an iframe and the browser (Safari / strict-tracking Firefox /
 *      Chrome incognito) blocks "third-party" SameSite=None cookies.
 *
 *   Either mechanism is enough on its own — the backend's `get_current_user`
 *   reads the cookie first and falls back to `Authorization: Bearer`.
 */
import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;
if (!BASE) {
  // Surface this loudly in the console — without it the app cannot work.
  // eslint-disable-next-line no-console
  console.error('REACT_APP_BACKEND_URL is missing from .env');
}

export const API = `${BASE}/api`;

/**
 * Storage of the access token.
 *
 * Architecture note (re: security audits):
 *   - Auth is PRIMARILY cookie-based (`withCredentials: true` below).
 *     Cookies are set by the backend with HttpOnly + Secure + SameSite,
 *     which is the safer pattern.
 *   - localStorage is a documented FALLBACK because Capacitor's WebView
 *     and Safari ITP block third-party cookies in many real-world
 *     scenarios (preview iframes, cross-origin embeds, etc.). Without
 *     this fallback the app cannot stay signed in on iOS or in our
 *     own preview environment.
 *   - The token in localStorage is the SAME short-lived JWT (1 day TTL
 *     per `auth.py:ACCESS_TTL_MIN`) so the blast radius of an XSS
 *     extraction is bounded to one day. Removing localStorage would
 *     break mobile and ITP users today.
 *   - All `try/catch` here is for browsers/private modes where
 *     localStorage throws on access (Safari Private, some embed views).
 */
const TOKEN_KEY = 'lf_access_token';

export function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}

export function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* storage disabled — fall back to cookies only */ }
}

export const http = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach the Bearer token (if we have one) to every request. This is a
// no-op when cookies are working; when cookies are blocked it keeps auth alive.
http.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Best-effort message extraction from FastAPI errors. */
export function apiErrorMessage(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || 'Something went wrong';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(', ');
  if (typeof d === 'object' && d.msg) return d.msg;
  return String(d);
}
